/**
 * @fileoverview Hook Actions Layer para gestión de cambio de estado de pago de órdenes de cliente en Inbox.
 * @module useCustomerOrderPaymentStatusActions
 * @category Hook - Actions Layer
 * @since 1.0.0
 * @version 1.0.0
 */
import { useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { isEmpty } from "lodash";
import orderShopModelMC from '../../../models/OrderShopModelMC';
import { LOAD_DATA_FAILURE } from '../../../../actions/inbox/conversationActions';

/**
 * Hook Actions Layer para gestión de cambio de estado de pago de órdenes de cliente.
 *
 * @function useCustomerOrderPaymentStatusActions
 * @param {Object} order - Objeto de la orden
 * @param {Function} updateOrderInList - Callback para actualizar la orden en la lista
 * @returns {Object} loading, openModal, newStatus, handleStatusChange, setOpenModal, setNewStatus
 * @see useCustomerOrderPaymentStatusUI
 */
export default function useCustomerOrderPaymentStatusActions(order, updateOrderInList) {
  const dispatch = useDispatch();
  const [newStatus, setNewStatus] = useState('');
  const [openModal, setOpenModal] = useState(false);
  const [loading, setLoading] = useState(false);

  /**
   * Maneja el cambio de estado de pago de la orden.
   * @param {string} _newStatus - Nuevo estado de pago
   * @returns {Promise<void>}
   */
  const handleStatusChange = useCallback(async (_newStatus) => {
    setNewStatus(_newStatus);
    if (isEmpty(order.payment_transaction)) {
      setOpenModal(true);
    } else {
      setLoading(true);
      const orderData = {
        payment_status: _newStatus,
        payment_transactions_attributes: [{
          id: order.payment_transaction.id,
          status: _newStatus.replace('payment_', '')
        }]
      };
      try {
        const response = await orderShopModelMC.save({ id: order.web_id, order: orderData });
        updateOrderInList(response.order);
      } catch (error) {
        dispatch({
          type: LOAD_DATA_FAILURE,
          error: error?.message || 'Error updating payment status'
        });
      } finally {
        setLoading(false);
      }
    }
  }, [order, updateOrderInList, dispatch]);

  return {
    loading,
    openModal,
    newStatus,
    handleStatusChange,
    setOpenModal,
    setNewStatus
  };
}
